import { type DataContainer, type Feature, type FeatureStatus, submitMerge } from "@otto-ec/tracking-bct";
import { captureErrorWithPayload } from "utils/error";
import { preventClickBubbling, registerVisibilityObserver } from "utils/events";
import { createLogger } from "utils/logging";
import { generateRandomString } from "utils/utils";
import { BenefitElement, defineElement, buildSearchParams, callBackend } from "../benefit_base";
import type { HTMLOcTagV1Element } from "@otto-ec/otto-components/tag";

const ATTRIBUTE_NAMES = {
	BENEFIT_ID: "benefit-id",
	FEATURE: "feature",
	TEAM: "team",
	VARIATION_ID: "variation-id",
	TARGET_CONTEXT_DRESON: "target-context-dreson",
	CURRENT_CONTEXT_DRESON: "current-context-dreson",
	SHOULD_SEND_TRACKING: "send-tracking",
	TRACKING_PARENT_ID: "tracking-parent-id",
	SEND_EVENTS: "send-events", /*                  */
	REMOVE_EXPIRED: "remove-expired",
	LOAD_ON_VISIBILITY: "load-on-visibility",
	DATA: "data",
};

const BENEFIT_TAG_NAME = "ft9-benefit-tag-v1";

const REQUEST_INITIATOR_FILE_NAME = new URL(import.meta.url).pathname.split("/").at(-1) ?? "unknown";

const ERROR_KEY = "ft9-benefit-tag-not-ok";
const ERROR_LABELS = { service: "tag", module: "tag", team: "ft9" };
const PROMO_FEATURE_LABEL = "promo_Feature";

const log = createLogger("tag", "tag");

export class BenefitTagElement extends BenefitElement {
	/*                                                                  */
	protected override async onInit(): Promise<void> {
		preventClickBubbling(this);
	}

	protected async hydrate(): Promise<void> {
		const data = this.getAttribute(ATTRIBUTE_NAMES.DATA);
		if (data) {
			const success = this.renderHtml(data);
			if (success) {
				return;
			}
			log.warn("Failed to render from data attribute, falling back to API call");
		}

		await this.fetchAndRender();
	}

	private async fetchAndRender(): Promise<void> {
		this.replaceChildren(); /*                       */

		if (this.hasAttribute(ATTRIBUTE_NAMES.LOAD_ON_VISIBILITY)) {
			registerVisibilityObserver(this, async () => {
				this.removeAttribute(ATTRIBUTE_NAMES.LOAD_ON_VISIBILITY);
				await this.fetchAndRender();
			});
			return;
		}

		const targetContextDreson = this.getAttribute(ATTRIBUTE_NAMES.TARGET_CONTEXT_DRESON);
		if (targetContextDreson === "preview-dreson") {
			/*                                                         */
			return;
		}

		if (!targetContextDreson && !this.hasAttribute(ATTRIBUTE_NAMES.BENEFIT_ID) && !this.hasAttribute(ATTRIBUTE_NAMES.VARIATION_ID)) {
			log.error("No psr, benefitId nor variationId provided, cannot load tag.");
			captureErrorWithPayload({ message: "No psr, benefitId nor variationId provided", type: ERROR_KEY, labels: ERROR_LABELS });
			return;
		}

		const params = {
			benefitId: this.getAttribute(ATTRIBUTE_NAMES.BENEFIT_ID),
			variationId: this.getAttribute(ATTRIBUTE_NAMES.VARIATION_ID),
			targetContextPsr: this.getAttribute(ATTRIBUTE_NAMES.TARGET_CONTEXT_DRESON),
		};
		const urlSearchParams = buildSearchParams(params);
		log.info("Calling tag for %s with %s", this.id || "id-not-set", urlSearchParams.toString());

		try {
			const url = new URL(`/benefit-tag/tag?${urlSearchParams}`, globalThis.location.origin);
			const options = {
				url,
				feature: this.getAttribute(ATTRIBUTE_NAMES.FEATURE),
				team: this.getAttribute(ATTRIBUTE_NAMES.TEAM),
				initiator: REQUEST_INITIATOR_FILE_NAME,
				priority: "auto" as const,
				logging: {
					logger: log,
					errorKey: ERROR_KEY,
					errorLabels: ERROR_LABELS,
				},
			};
			const response = await callBackend(options);
			const backendData = await response.text();
			this.renderFromResponse(backendData);
		} catch {
			/*                                                                    */
			/*                                                                          */
		}
	}

	private renderFromResponse(html: string): boolean {
		if (html.length === 0) return false;
		return this.renderHtml(html);
	}

	private renderHtml(html: string): boolean {
		const template = document.createElement("template");
		template.innerHTML = html;

		const tag = template.content.firstElementChild;
		if (!(tag instanceof HTMLElement) || tag.localName !== "oc-tag-v1") {
			log.warn("No valid root element in response");
			return false;
		}
		const ocTag = tag as HTMLOcTagV1Element;

		this.applySheetUrl(ocTag);
		this.applyPricingItemSizeQuirk(ocTag);

		/*                                                                                            */
		this.replaceChildren(...template.content.childNodes);

		this.initTag(ocTag);
		return true;
	}

	private applySheetUrl(ocTag: HTMLOcTagV1Element): void {
		const benefitId = ocTag.dataset.benefitId;
		if (!benefitId || !this.getAttribute(ATTRIBUTE_NAMES.FEATURE) || ocTag.getAttribute("data-oc-sheet-v1-create.base64-url")) {
			return;
		}
		/*                                        */
		const sheetUrl = this.buildSheetUrl(benefitId);
		ocTag.setAttribute("data-oc-sheet-v1-create.base64-url", btoa(sheetUrl));
	}

	private applyPricingItemSizeQuirk(ocTag: HTMLOcTagV1Element): void {
		/*                                                           */
		/*                                                                                                              */
		/*                                                                                                                      */
		/*                                                                         */
		/*                                                                                                             */
		/*                                              */
		if (this.getAttribute("slot") !== "benefit-tag") {
			return;
		}
		const pricingElement = this.closest("ofc-pricing-item-v1");
		if (!pricingElement) {
			return;
		}
		const size = pricingElement.getAttribute("display-size") ?? "100";
		ocTag.setAttribute("size", size === "200" ? "50" : "25");
	}

	private buildSheetUrl(benefitId: string): string {
		/*                                                                                              */
		/*                                                                                       */
		const originFeature = this.getAttribute(ATTRIBUTE_NAMES.FEATURE) ?? "";
		let sheetUrl = `/benefit-sheet/sheet/${encodeURIComponent(originFeature)}/${encodeURIComponent(benefitId)}?openTriggeredBy=ClickOnTag`;

		const targetContextPsr = this.getAttribute(ATTRIBUTE_NAMES.TARGET_CONTEXT_DRESON);
		const currentContextPsr = this.getAttribute(ATTRIBUTE_NAMES.CURRENT_CONTEXT_DRESON);
		if (targetContextPsr) {
			sheetUrl += `&contextPsr=${encodeURIComponent(targetContextPsr)}`;
		} else if (currentContextPsr) {
			sheetUrl += `&contextPsr=${encodeURIComponent(currentContextPsr)}`;
		}

		return sheetUrl;
	}

	private initTag(tag: HTMLOcTagV1Element) {
		/*                                              */
		if (this.hasAttribute(ATTRIBUTE_NAMES.REMOVE_EXPIRED) && tag.dataset.expirationDate) {
			this.triggerReloadAt(tag.dataset.expirationDate);
		}

		/*              */
		const trackingParentId = this.getAttribute(ATTRIBUTE_NAMES.TRACKING_PARENT_ID);
		this.sendTracking(tag, trackingParentId);
	}

	private sendTracking(tag: HTMLElement, trackingParentId: string | null) {
		if (!this.hasAttribute(ATTRIBUTE_NAMES.SHOULD_SEND_TRACKING)) {
			return;
		}

		const trackingData = tag.dataset.trackingLabels;
		if (!trackingData) {
			return;
		}

		let labels = JSON.parse(trackingData) as DataContainer;
		if (trackingParentId) {
			const { [PROMO_FEATURE_LABEL]: _promoFeature, ...remainingLabels } = labels;
			labels = remainingLabels;
		}

		let feature = {
			id: generateRandomString(20),
			name: "Benefit",
			status: "loaded" as FeatureStatus,
			labels,
		} as Feature;

		if (trackingParentId) {
			feature = {
				...feature,
				parentId: trackingParentId,
			};
		}

		submitMerge({}, [feature]);
	}
}

defineElement(BenefitTagElement, BENEFIT_TAG_NAME, "ftnine_emergency_custom_element_disable_tag");
